import { converterNumeroBR } from '../lib/textoBR'

// Comissão aceita "5", "5%", "5,0", "12,5%"; null se inválida (não numérica ou fora de 0..100).
export function converterComissao(texto: string): number | null {
  const n = converterNumeroBR(texto.replace(/%/g, '').trim())
  if (n === null || n < 0 || n > 100) return null
  return n
}

// Quantidade da regra do preco ("este valor so vale de 24 unidades para cima").
// Tres respostas, e a diferenca entre duas delas importa:
//   undefined = campo VAZIO, que nao e erro -- e o normal, e significa "este
//               preco nao tem regra". Toda planilha anterior a 17/08/2026 esta
//               assim, e para elas nada pode mudar.
//   null      = campo PREENCHIDO com sujeira (nao numerico, zero ou negativo).
//   number    = a quantidade.
// Gemea de converterComissao, logo acima, e pelo mesmo motivo: texto de planilha
// no padrao brasileiro que vira numero com regra de negocio junto.
export function converterQuantidadeRegra(texto: string): number | null | undefined {
  if (texto.trim() === '') return undefined
  const n = converterNumeroBR(texto)
  if (n === null || n <= 0) return null
  return n
}

// Gemea de converterQuantidadeRegra, com uma regra a mais: caixa tem numero
// INTEIRO de unidades dentro. Tres respostas, e a diferenca importa:
//   undefined = vazio, que e o normal e nao e erro
//   null      = preenchido com sujeira (nao numerico, <= 0, ou fracionario, e
//               sem o padrao "NxPESO" abaixo)
//   number    = o fator. O `1` E VALIDO: sao 19 produtos da MAVALERIO.
export function converterFatorEmbalagem(texto: string): number | null | undefined {
  if (texto.trim() === '') return undefined
  const n = converterNumeroBR(texto)
  if (n !== null && n > 0 && Number.isInteger(n)) return n
  // ⭐ Achado real dela em 17/09: varios fornecedores escrevem a quantidade da
  // embalagem JUNTO com o peso da unidade na mesma celula -- "6X400G" quer
  // dizer 6 unidades de 400g cada -- em vez de um numero puro. Recorrente nas
  // tabelas reais dela, nao caso raro: 214 linhas reprovadas numa unica
  // importacao antes deste fallback. So entra em jogo quando o numero puro
  // falhou -- nunca sobrescreve um numero ja valido.
  const doPadraoNxPeso = extrairFatorDoPadraoNxPeso(texto)
  if (doPadraoNxPeso !== null) return doPadraoNxPeso
  // ⭐ Segundo achado dela, no mesmo dia, depois do conserto acima: 24 linhas
  // continuaram reprovadas com celulas tipo "4,80KG" -- produto vendido SO EM
  // UNIDADE, sem caixa. Palavras dela: "esses produtos sao unidades, nao vem
  // em caixa". A celula nao e quantidade de embalagem nenhuma, e o peso/volume
  // da propria unidade -- mesmo sentido de celula vazia (undefined), nunca erro.
  if (pareceApenasPesoOuVolumeDaUnidade(texto)) return undefined
  return null
}

function extrairFatorDoPadraoNxPeso(texto: string): number | null {
  const m = texto.match(/(\d+)\s*[xX]\s*\d/)
  if (!m) return null
  const n = Number(m[1])
  return Number.isInteger(n) && n > 0 ? n : null
}

function pareceApenasPesoOuVolumeDaUnidade(texto: string): boolean {
  return /^\d+([.,]\d+)?\s*(kg|g|l|ml)$/i.test(texto.trim())
}

// ⭐ R12, DECISAO DELA (29/08): na coluna de preco avulso, `0` quer dizer
// "este produto NAO vende unidade solta" -- e campo vazio, nao preco de R$ 0,00.
// Terceira gemea do formato de tres respostas, e aqui a diferenca custa dinheiro:
//   undefined = esta linha NAO TEM preco avulso. Celula vazia OU zero, e
//               NENHUM DOS DOIS e erro. Quem le isso mais adiante
//               (payloadCaixaFechada) manda `null` ao banco: "apague o preco
//               avulso deste produto".
//   null      = ERRO DE LINHA (`preco_unidade_invalido`): negativo ou ilegivel.
//               ⚠ Negativo nunca e decisao de negocio, e dedo trocado.
//   number    = o preco da unidade solta.
//
// ⛔ POR QUE EXISTE: ate 29/08 a validacao so recusava texto ILEGIVEL, entao
// `'0'` e `'-5,00'` passavam como preco valido -- enquanto paridadePreco.ts lia
// o MESMO `0` como "nao vende avulso". As duas camadas discordavam do sentido do
// zero, e o check da migration 0021 e `>= 0`: o zero chegaria ao banco e a
// unidade solta seria vendida DE GRACA. Agora a leitura e uma so, e mora aqui.
export function converterPrecoUnidade(texto: string): number | null | undefined {
  if (texto.trim() === '') return undefined
  const n = converterNumeroBR(texto)
  if (n === null || n < 0) return null
  // ⚠ ESTA LINHA E A DECISAO DELA, e ela e SEPARADA do teste do negativo de
  // proposito: juntar as duas num `n <= 0` -- que e como a gemea
  // `converterFatorEmbalagem` faz -- transformaria preco negativo em "nao vende
  // avulso" em vez de erro de linha. Aqui as duas coisas nao se confundem.
  if (n === 0) return undefined
  return n
}

// Percentual de tributo (IPI, ICMS, ST). Tres respostas, como
// converterQuantidadeRegra logo acima, e pela mesma razao: campo VAZIO e o
// normal e precisa se distinguir de campo preenchido com sujeira.
//   undefined = vazio, e nao e erro. E o caso dos 727 produtos de hoje.
//   null      = preenchido com sujeira (nao numerico, ou fora de 0..100).
//   number    = o percentual.
//
// ⚠ ZERO E VALIDO aqui, ao contrario da quantidade: "IPI 0%" e informacao
// fiscal legitima -- o produto e isento -- enquanto "quantidade minima 0" nao
// quer dizer nada. Por isso o limite e `< 0`, e nao `<= 0`. Copiar a linha da
// quantidade sem trocar isso transformaria "isento" em erro de planilha.
export function converterPercentualTributo(texto: string): number | null | undefined {
  if (texto.trim() === '') return undefined
  const n = converterNumeroBR(texto.replace(/%/g, '').trim())
  if (n === null || n < 0 || n > 100) return null
  return n
}

export interface LinhaMapeada {
  indice: number // 1-based (linha da planilha apos o cabecalho)
  campos: Record<string, string>
}

// O QUE ha de errado na linha, separado de COMO isso se le. O `motivo` embute o
// valor ofensivo ("preco invalido: \"abc\""), entao duas linhas com o mesmo
// problema e valores diferentes tem motivos diferentes -- e agrupar por texto
// nao agruparia nada. O `tipo` e o que permite dizer "137 linhas com preco
// invalido" em vez de 137 mensagens quase iguais.
export type TipoErroLinha =
  | 'codigo_vazio'
  | 'descricao_vazia'
  | 'preco_invalido'
  | 'comissao_invalida'
  | 'quantidade_minima_invalida'
  | 'quantidade_maxima_invalida'
  | 'quantidade_maxima_menor_que_minima'
  | 'fator_invalido'
  | 'preco_unidade_invalido'
  | 'ipi_invalido'
  | 'icms_invalido'
  | 'st_invalido'
  | 'pis_cofins_invalido'
  | 'razao_social_vazia'
  // Alíquotas fiscais (validarAliquotas.ts, Task 8). Vivem no MESMO union, e
  // não num tipo próprio, para o erro continuar passando pela previa e por
  // agruparErros já existentes sem nenhum dos dois saber que a planilha é
  // fiscal.
  | 'tipo_de_linha_desconhecido'
  | 'codigo_produto_vazio'
  | 'uf_destino_vazia'
  | 'uf_destino_invalida'
  | 'aliquota_pct_ausente'
  | 'aliquota_pct_invalida'
  | 'mva_pct_ausente'
  | 'mva_pct_invalido'
  | 'regiao_destino_vazia'
  | 'incoterm_vazio'
  | 'incoterm_invalido'
  | 'desconto_pct_ausente'
  | 'desconto_pct_invalido'

export interface ErroDeLinha {
  linha: number
  motivo: string
  tipo: TipoErroLinha
  // O texto da planilha que causou o erro, quando ha um. Serve de exemplo no
  // agrupamento: "linha 12 (\"abc\")" diz o que consertar; so "linha 12" nao.
  valor?: string
}

export interface ResultadoValidacao {
  validas: LinhaMapeada[]
  erros: ErroDeLinha[]
}

export function aplicarMapa(
  colunas: string[],
  linhas: string[][],
  mapa: Record<string, string>,
): LinhaMapeada[] {
  // O mapa e chaveado pela POSICAO da coluna, nao pelo nome: nome de coluna nao
  // e unico (a planilha "PROMOÇÃO DE FARINHAS" tem duas colunas de codigo) e,
  // por nome, as duas caiam no mesmo campo -- a de indice maior sobrescrevia a
  // outra, sem aviso. Achado 4 do smoke de 2026-07-27.
  return linhas.map((linha, i) => {
    const campos: Record<string, string> = {}
    for (let j = 0; j < colunas.length; j++) {
      const campo = mapa[String(j)]
      if (campo) campos[campo] = (linha[j] ?? '').trim()
    }
    return { indice: i + 1, campos }
  })
}

// Codigo repetido NAO e erro: e a outra condicao do mesmo produto (mesmo item
// vendido a preco/comissao diferentes, as duas no mesmo arquivo). Quem separa
// as condicoes e agruparCondicoes (condicoes.ts), que roda ANTES daqui e
// entrega cada condicao com codigos unicos -- entao a checagem que existia
// aqui, alem de desnecessaria, reprovaria a condicao escolhida pela usuaria.
export function validarProdutos(linhas: LinhaMapeada[]): ResultadoValidacao {
  const validas: LinhaMapeada[] = []
  const erros: ResultadoValidacao['erros'] = []
  for (const linha of linhas) {
    const { codigo, descricao, preco, comissao } = linha.campos
    const minimaTexto = linha.campos.quantidade_minima ?? ''
    const maximaTexto = linha.campos.quantidade_maxima ?? ''
    const minima = converterQuantidadeRegra(minimaTexto)
    const maxima = converterQuantidadeRegra(maximaTexto)
    const fatorTexto = linha.campos.unidades_por_embalagem ?? ''
    const fator = converterFatorEmbalagem(fatorTexto)
    const precoUnidadeTexto = linha.campos.preco_unidade ?? ''
    // ⚠ So o `null` vira erro la embaixo, e nunca o `undefined`: `undefined` e
    // "esta linha nao vende avulso" (celula vazia OU zero, R12). Testar com `!`
    // ou com `== null` juntaria os dois e reprovaria a planilha inteira.
    const precoUnidade = converterPrecoUnidade(precoUnidadeTexto)
    // ⚠ O NCM NAO aparece aqui de proposito: ele entra como vier, sem
    // validacao de formato (decisao da spec). Nao ha `ncm_invalido`.
    const ipiTexto = linha.campos.ipi_pct ?? ''
    const icmsTexto = linha.campos.icms_pct ?? ''
    const stTexto = linha.campos.st_pct ?? ''
    const pisCofinsTexto = linha.campos.pis_cofins_pct ?? ''
    const ipi = converterPercentualTributo(ipiTexto)
    const icms = converterPercentualTributo(icmsTexto)
    const st = converterPercentualTributo(stTexto)
    const pisCofins = converterPercentualTributo(pisCofinsTexto)
    if (!codigo) {
      erros.push({ linha: linha.indice, tipo: 'codigo_vazio', motivo: 'código vazio' })
    } else if (!descricao) {
      erros.push({ linha: linha.indice, tipo: 'descricao_vazia', motivo: 'descrição vazia' })
    } else if (preco && converterNumeroBR(preco) === null) {
      erros.push({
        linha: linha.indice, tipo: 'preco_invalido', valor: preco,
        motivo: `preço inválido: "${preco}"`,
      })
    } else if (comissao && converterComissao(comissao) === null) {
      erros.push({
        linha: linha.indice, tipo: 'comissao_invalida', valor: comissao,
        motivo: `comissão inválida: "${comissao}"`,
      })
    } else if (fator === null) {
      erros.push({
        linha: linha.indice, tipo: 'fator_invalido', valor: fatorTexto,
        motivo: `unidades por embalagem inválida: "${fatorTexto}"`,
      })
    } else if (precoUnidade === null) {
      erros.push({
        linha: linha.indice, tipo: 'preco_unidade_invalido', valor: precoUnidadeTexto,
        motivo: `preço avulso inválido: "${precoUnidadeTexto}"`,
      })
    } else if (minima === null) {
      erros.push({
        linha: linha.indice, tipo: 'quantidade_minima_invalida', valor: minimaTexto,
        motivo: `quantidade mínima inválida: "${minimaTexto}"`,
      })
    } else if (maxima === null) {
      erros.push({
        linha: linha.indice, tipo: 'quantidade_maxima_invalida', valor: maximaTexto,
        motivo: `quantidade máxima inválida: "${maximaTexto}"`,
      })
    } else if (minima !== undefined && maxima !== undefined && maxima < minima) {
      // ⚠ Regra que NENHUMA quantidade satisfaz. Deixar passar poria no banco um
      // preco que avisa para sempre, sem saida. A constraint da 0018 tambem
      // recusa, mas la o erro sai em ingles e sem dizer QUAL linha do arquivo.
      erros.push({
        linha: linha.indice,
        tipo: 'quantidade_maxima_menor_que_minima',
        valor: `${maximaTexto} < ${minimaTexto}`,
        motivo: `quantidade máxima (${maximaTexto}) é menor que a mínima (${minimaTexto})`,
      })
    } else if (ipi === null) {
      erros.push({
        linha: linha.indice, tipo: 'ipi_invalido', valor: ipiTexto,
        motivo: `IPI inválido: "${ipiTexto}"`,
      })
    } else if (icms === null) {
      erros.push({
        linha: linha.indice, tipo: 'icms_invalido', valor: icmsTexto,
        motivo: `ICMS inválido: "${icmsTexto}"`,
      })
    } else if (st === null) {
      erros.push({
        linha: linha.indice, tipo: 'st_invalido', valor: stTexto,
        motivo: `ST inválido: "${stTexto}"`,
      })
    } else if (pisCofins === null) {
      erros.push({
        linha: linha.indice, tipo: 'pis_cofins_invalido', valor: pisCofinsTexto,
        motivo: `PIS/COFINS inválido: "${pisCofinsTexto}"`,
      })
    } else {
      validas.push(linha)
    }
  }
  return { validas, erros }
}

export function validarClientes(linhas: LinhaMapeada[]): ResultadoValidacao {
  const validas: LinhaMapeada[] = []
  const erros: ResultadoValidacao['erros'] = []
  for (const linha of linhas) {
    if (!linha.campos.razao_social) {
      erros.push({ linha: linha.indice, tipo: 'razao_social_vazia', motivo: 'razão social vazia' })
    } else {
      validas.push(linha)
    }
  }
  return { validas, erros }
}
